import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Link as LinkIcon, Copy, Check, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * One-click "Send Payment Link" generator.
 *
 * Renders a small popover with:
 *   - Payment type selector (trial_session / single_session / intensive_package)
 *   - Copy Link button (auto-copies on open, icon flips to Check for 2s after)
 *   - Send via WhatsApp button (opens wa.me with a pre-filled message)
 *
 * No state travels to the backend — the generated URL embeds name/email/type
 * and PaymentPage.jsx reads them from query params.
 */

const PAYMENT_TYPES = [
  { value: 'trial_session', label: 'Trial Session' },
  { value: 'single_session', label: '1:1 Session' },
  { value: 'intensive_package', label: 'Intensive Package' },
];

const buildPaymentUrl = ({ type, name, email }) => {
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) ||
    process.env.REACT_APP_BACKEND_URL ||
    '';
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (name) params.set('name', name);
  if (email) params.set('email', email);
  const qs = params.toString();
  return `${origin}/pay${qs ? `?${qs}` : ''}`;
};

const buildWhatsAppHref = ({ phone, url }) => {
  const message = `Hello, please complete your booking using the secure payment link below:\n${url}`;
  // Strip all non-digits from phone; if empty, wa.me still opens for the user to pick a contact
  const target = (phone || '').replace(/\D/g, '');
  return `https://wa.me/${target}?text=${encodeURIComponent(message)}`;
};

const SendPaymentLinkButton = ({
  studentName,
  studentEmail,
  studentPhone,
  buttonSize = 'sm',
  buttonVariant = 'outline',
  testIdSuffix = '',
}) => {
  const [open, setOpen] = useState(false);
  const [paymentType, setPaymentType] = useState('trial_session');
  const [copied, setCopied] = useState(false);

  const paymentUrl = useMemo(
    () =>
      buildPaymentUrl({
        type: paymentType,
        name: studentName,
        email: studentEmail,
      }),
    [paymentType, studentName, studentEmail]
  );

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(paymentUrl);
      } else {
        const ta = document.createElement('textarea');
        ta.value = paymentUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Payment link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Could not copy link. Please copy it manually from the field above.');
    }
  };

  const handleWhatsApp = () => {
    const href = buildWhatsAppHref({ phone: studentPhone, url: paymentUrl });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const testId = (name) => (testIdSuffix ? `${name}-${testIdSuffix}` : name);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          className="gap-2"
          data-testid={testId('send-payment-link-button')}
        >
          <LinkIcon className="h-4 w-4" />
          Send Payment Link
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 bg-white"
        align="end"
        data-testid={testId('send-payment-link-popover')}
      >
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Generate payment link
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {studentName || 'Unknown'} · {studentEmail || 'no email'}
            </p>
          </div>

          <div>
            <Label htmlFor={`payment-type-${testIdSuffix || 'default'}`}>
              Payment Type
            </Label>
            <select
              id={`payment-type-${testIdSuffix || 'default'}`}
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded-md p-2 bg-white text-sm"
              data-testid={testId('send-payment-link-type')}
            >
              {PAYMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs text-gray-600">Link</Label>
            <div
              className="mt-1 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs font-mono break-all text-gray-700"
              data-testid={testId('send-payment-link-url')}
            >
              {paymentUrl}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5"
              onClick={handleCopy}
              data-testid={testId('send-payment-link-copy')}
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  Copy Link
                </>
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              className="flex-1 gap-1.5 bg-[#25D366] hover:bg-[#1fb959] text-white"
              onClick={handleWhatsApp}
              data-testid={testId('send-payment-link-whatsapp')}
            >
              <Send className="h-3.5 w-3.5" />
              WhatsApp
            </Button>
          </div>

          {!studentPhone && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              No phone on file — WhatsApp will open without a recipient; paste
              the link or pick a contact manually.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SendPaymentLinkButton;
